<template>
  <nav class="tenant-navbar" role="navigation" :aria-label="brandName" :style="brandStyle">
    <router-link :to="{ name: 'home' }" class="brand-link">
      <img v-if="logoUrl" class="brand-logo" :src="logoUrl" :alt="brandName" />

      <!--
        Nothing is rendered while the request is in flight. `brandName` falls back to
        the platform name, and painting that for the duration of the round trip would
        show every visitor the apex brand's name for a frame before swapping it for
        the clinic's -- the exact bug the per-host bootstrap payload existed to avoid.
      -->
      <span
        v-if="loading"
        class="brand-skeleton"
        data-test="navbar-skeleton"
        aria-hidden="true"
      ></span>
      <span v-else class="brand-name" :title="seoTitle">{{ brandName }}</span>
    </router-link>

    <!--
      The one login on the site, and the only one that matters for this navbar.

      `MainLayout` renders this component on the home route and `MainMenu`
      everywhere else, and `MainMenu` carries its own login link. So without an
      entry here, `/` would be the single page on the site from which nobody can
      reach the panel: `SiteFooter` is suppressed on the home route too, which
      leaves no second way in.

      It is an absolute anchor to the single apex login rather than an SPA
      route, because the history base is the brand prefix (`/<slug>/`): a
      `router-link to login` would resolve to `/<slug>/login` and keep the
      visitor inside the brand, while authentication is centralised on the apex
      (`POST /users/login`, one identity store). `MainMenu` carries the same
      absolute link, so there is still exactly one login form and one endpoint
      behind it. That endpoint applies no role filter and `routes/panel.ts`
      routes each role after sign-in, so a platform administrator, a brand
      administrator, a professional and a requester all arrive here and all get
      the account they are entitled to.

      The label moved to the `title` attribute: the entry is icon-only by
      request, on every viewport, and an icon with no accessible name reads as
      decoration to assistive tech -- the `title` is what keeps the control
      named ("Acceso Profesional") without printing a word beside the icon.
    -->
    <a
      :href="apexLoginUrl"
      class="navbar-login-link"
      data-test="navbar-login"
      :title="$t('website.menu.professionalAccess')"
      :aria-label="$t('website.menu.professionalAccess')"
    >
      <i class="fas fa-user-circle" aria-hidden="true"></i>
    </a>
  </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { fetchCurrentTenant, type CurrentTenant } from '@/apis/tenant.api';
import { apexLoginUrl as apexLoginUrlFor } from '@/services/platform.service';

// The apex host comes from the per-host branding payload (in the DOM before the
// bundle mounts). Resolved lazily so it is read from the live document rather
// than frozen at import time.
const apexLoginUrl = computed(() => apexLoginUrlFor());

// What the navbar shows when the endpoint cannot answer. On the apex that string is
// also the correct name, so the failure state and the platform state agree instead of
// leaving the bar blank. The official platform name (see `SiteConfig::DEFAULT_NAME`
// server-side); the host/domain itself stays `bioterapias.cl`.
const PLATFORM_NAME = 'DispoApp';

// Six hex digits, optionally prefixed. Deliberately stricter than the CSS the value
// is interpolated into: this string arrives over the network from per-host tenant
// data, and anything that is not a plain colour must never reach a style attribute.
const HEX_COLOR = /^#?[0-9a-fA-F]{6}$/;

const tenant = ref<CurrentTenant | null>(null);
const loading = ref(true);

// Normalize tenant name: replace underscores and hyphens with spaces
const normalizeTenantName = (name: string | null | undefined): string =>
  (name || '').replace(/[-_]+/g, ' ').trim();

const brandName = computed(() => normalizeTenantName(tenant.value?.name)?.trim() || PLATFORM_NAME);

const seoTitle = computed(() => tenant.value?.seo?.title?.trim() || brandName.value);

const logoUrl = computed(() => tenant.value?.settings?.logo_url || null);

const brandColor = computed(() => {
  const settings = tenant.value?.settings;
  if (!settings) return null;
  return firstValidColor(settings.primary_color, settings.secondary_color);
});

const navbarColor = computed(() => {
  const settings = tenant.value?.settings;
  if (!settings) return null;
  return firstValidColor(settings.navbar_color, settings.primary_color, settings.secondary_color);
});

function firstValidColor(...candidates: (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const hex = candidate.trim();
    if (HEX_COLOR.test(hex)) return hex.startsWith('#') ? hex : `#${hex}`;
  }
  return null;
}

const brandStyle = computed(() => {
  const css: Record<string, string> = {};
  if (navbarColor.value) {
    css['--navbar-bg'] = navbarColor.value;
  }
  const color = brandColor.value;
  if (color) {
    css['--brand-primary'] = color;
  }
  return css;
});

onMounted(async () => {
  try {
    const { data } = await fetchCurrentTenant();
    tenant.value = data;
  } catch {
    // A navbar is chrome: it must render whatever it has. Swallowing the failure here
    // is the whole point -- `brandName` already falls back to the platform name.
    tenant.value = null;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.tenant-navbar {
  background: var(--navbar-bg, var(--brand-primary, #100414));
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  padding: 0.9rem 1.25rem;
  /* Brand on the left, login on the right. Without this the login would sit
     flush against the brand name on narrow screens instead of holding its own
     end of the bar. */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.navbar-brand,
.brand-link {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.brand-skeleton {
  display: inline-block;
  height: 1.1rem;
  width: 9rem;
  border-radius: 3px;
  background: rgb(255 255 255 / 18%);
}

.brand-link {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 600;
  text-decoration: none;
  /* `min-width: 0` is what lets the ellipsis on `.brand-name` engage at all:
     a flex item defaults to `min-width: auto`, so it refuses to shrink below its
     content and a long brand name would push the login off the end of the bar.
     The login carries `flex: 0 0 auto`, so this side is the only one that
     yields -- which is the correct priority: the way into the panel must stay
     reachable. */
  flex: 1 1 auto;
  min-width: 0;
}

.brand-logo {
  height: 2rem;
  width: 2rem;
  border-radius: 4px;
  object-fit: cover;
}

.brand-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Login entry. Icon-only: mirrors `MainMenu`'s `.login-icon-link`, with the
   pill treatment so the control still reads as a button. The accessible name
   lives on the link's `title`/`aria-label`. */
.navbar-login-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex: 0 0 auto;
  padding: 0.4rem 0.85rem;
  border-radius: 99px;
  border: 1px solid rgb(255 255 255 / 22%);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.navbar-login-link:hover {
  background: rgb(255 255 255 / 10%);
  border-color: rgb(255 255 255 / 45%);
  transform: scale(1.02);
}

.navbar-login-link i {
  font-size: 1.1rem;
  line-height: 1;
}

:global(.dark-mode) .navbar-login-link {
  color: #c084fc;
  border-color: rgb(192 132 252 / 45%);
}

/* Icon-only entry: there is no visible label to collapse at any breakpoint, so
   the old `max-width: 480px` label-hiding rule is gone with the label. */
</style>
